/**
 * Spec 200 — offline indicator (Fase 4 #6).
 *
 * Listens to `online`/`offline` window events and renders a banner
 * when the browser reports no connectivity. The relay client also
 * has its own reconnection logic (exponential backoff via the
 * RelayClient class), so this component is purely informational —
 * it tells the user "you're seeing cached data" without affecting
 * the sync pipeline.
 */
import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

import './OfflineBanner.css';

export function OfflineBanner() {
  // Default to true on SSR-ish environments where `navigator` may
  // not exist — better to assume online and let an actual offline
  // event flip the banner than show it spuriously on first paint.
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div className="offline-banner" data-testid="offline-banner" role="status" aria-live="polite">
      <WifiOff size={16} strokeWidth={2} aria-hidden="true" />
      <span>Sin conexión. Mostrando datos guardados.</span>
    </div>
  );
}
